/** Batch "Set launch policy" dialog for the Tours grid (a custom `dialog(ctx)`
 *  batch action).
 *
 *  Launch policies are tenant-scoped, so the operator first picks WHICH operator's
 *  policies to choose from (defaulting to the grid's tenant filter), then a policy.
 *  When the selection may span tenants (per `detectCrossTenant`) a
 *  `CrossTenantStrategyField` is shown and a strategy is required. Submit fires the
 *  generic `/tours/batch/` dispatch via `ctx.runStandard`; the server re-checks the
 *  cross-tenant condition and is authoritative (a 400 surfaces as a snackbar).
 *  A selection with tours of an easy tenant still under the outbound guard is refused as a
 *  whole (409 easy_go_live_required, nothing queued): the grid's refusal dialog names each
 *  tenant with a link to its easy setup's Go live, and the selection is kept.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { PickerOption } from '@/api/tours';
import * as toursApi from '@/api/tours';
import { CrossTenantStrategyField } from '@/components/DataGrid/CrossTenantStrategyField';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { detectCrossTenant } from '@/components/DataGrid/detectCrossTenant';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

/** Strategy tiers a launch policy supports (matchable, not creatable). */
const LAUNCH_POLICY_STRATEGIES = ['ignore_others', 'match_or_ignore'];

interface Props {
  ctx: BatchActionContext;
  /** The grid's current tenant filter (null when unfiltered). Used as the default
   *  source operator and to decide whether the selection is cross-tenant. */
  gridTenantId: number | null;
}

export function SetLaunchPolicyDialog({ ctx, gridTenantId }: Props) {
  // The operator whose launch policies we pick from (defaults to the grid filter).
  const [sourceOp, setSourceOp] = useState<TenantPickerValue | null>(
    gridTenantId != null ? { id: gridTenantId, name: '' } : null,
  );
  const [policy, setPolicy] = useState<PickerOption | null>(null);
  const [strategy, setStrategy] = useState<string | null>(null);

  const sourceOpId = sourceOp?.id ?? null;
  // The picker returns only the source operator's own policies, so the value's
  // tenant is the source operator.
  const crossTenant =
    sourceOpId != null &&
    detectCrossTenant(sourceOpId, ctx.selection, { gridTenantFilter: gridTenantId });

  const needsStrategy = crossTenant && !strategy;
  const canSubmit = sourceOpId != null && policy != null && !needsStrategy;

  const submit = () => {
    if (!policy) return;
    const params: Record<string, unknown> = { launch_policy: policy.id };
    if (crossTenant && strategy) params.cross_tenant_strategy = strategy;
    void ctx.runStandard(params);
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Set launch policy</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Apply a launch policy to {ctx.count.toLocaleString()} tour(s). This runs in the
            background.
          </Typography>

          {gridTenantId == null && (
            <TenantPicker
              value={sourceOp}
              onChange={(t) => {
                setSourceOp(t);
                setPolicy(null);
                setStrategy(null);
              }}
            />
          )}

          <RemoteAutocomplete<PickerOption>
            label="Launch policy"
            size="small"
            fetchOnEmpty
            value={policy}
            getOptionLabel={(o) => o.name ?? `#${o.id}`}
            getOptionKey={(o) => o.id}
            queryFn={async (q) => {
              if (sourceOpId == null) return [];
              return (await toursApi.picker(sourceOpId, 'launch_policies', q)).results;
            }}
            onChange={(v) => setPolicy(v as PickerOption | null)}
          />

          {crossTenant && (
            <CrossTenantStrategyField
              value={strategy}
              onChange={setStrategy}
              allowedStrategies={LAUNCH_POLICY_STRATEGIES}
              noun="launch policy"
            />
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} tours.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          Set launch policy
        </Button>
      </DialogActions>
    </Dialog>
  );
}
